import { ESRI, GOOGLE } from '@rsos/maps/constants/mapProviders';
import { captureException } from '@rsos/utils/sentry';

/**
 * Pan the Esri map view state to a specific coordinate.
 * @param {Object} mapInstance - The Esri map instance object.
 * @param {Number} latitude - The latitude coordinate.
 * @param {Number} longitude - The longitude coordinate.
 * @param {Number} zoom - The zoom level with 10 as the default zoom level.
 */
export const panEsriMapTo = ({
  mapInstance = null,
  latitude = null,
  longitude = null,
  zoom = 10,
}) => {
  if (!mapInstance || !latitude || !longitude) return;

  try {
    const view = mapInstance.view;

    view.goTo({ center: [longitude, latitude], zoom });
  } catch (error) {
    captureException(error);
  }
};

/**
 * Pan the Google map view state to a specific coordinate.
 * @param {Object} mapInstance - The Google map instance object.
 * @param {Number} latitude - The latitude coordinate.
 * @param {Number} longitude - The longitude coordinate.
 */
export const panGoogleMapTo = ({
  mapInstance = null,
  latitude = null,
  longitude = null,
}) => {
  if (!mapInstance || !latitude || !longitude) return;

  try {
    mapInstance.panTo({ lat: latitude, lng: longitude });
  } catch (error) {
    captureException(error);
  }
};

/**
 * Set the Google map zoom level.
 * @param {Object} mapInstance - The Esri map instance object.
 * @param {Number} zoom - The zoom level with 10 as the default zoom level.
 */
export const setGoogleMapZoomTo = ({ mapInstance, zoom = 10 }) => {
  if (!mapInstance) return;

  try {
    mapInstance.setZoom(zoom);
  } catch (error) {
    captureException(error);
  }
};

/**
 * Pan the map view state to a specific coordinate.
 * @param {String} mapProvider - The map provider.
 * @param {Object} mapInstance - The map instance object.
 * @param {Number} latitude - The latitude coordinate.
 * @param {Number} longitude - The longitude coordinate.
 * @param {Number} zoom - The zoom level with 10 as the default zoom level.
 */
export const panMapTo = ({
  mapProvider = '',
  mapInstance = null,
  latitude = null,
  longitude = null,
  zoom = 10,
}) => {
  try {
    if (!mapProvider || !mapInstance || !latitude || !longitude) return;

    if (mapProvider === ESRI) {
      panEsriMapTo({ mapInstance, latitude, longitude, zoom });
    } else if (mapProvider === GOOGLE) {
      panGoogleMapTo({ mapInstance, latitude, longitude });
      setGoogleMapZoomTo({ mapInstance, zoom });
    }
  } catch (error) {
    captureException(error);
  }
};
